export type WaitlistDeployment = "CLOUD" | "ONPREM";

export type WaitlistInput = {
  name: string;
  email: string;
  company: string;
  deployment: WaitlistDeployment;
  comment: string | null;
};

export const landingCopy = {
  brand: "PinWiki",
  tagline: "Платформа знаний",
  login: "Войти",
  cta: "Запросить пилот",
  ctaSecondary: "Смотреть продукт",
  heroEyebrow: "Корпоративная платформа знаний для HR, юристов, поддержки и ИБ",
  heroTitle: "Знания компании, которым можно верить",
  heroLead:
    "Сотрудник получает ответ только по документам, которые ему разрешено читать, — со ссылкой на первоисточник. Не чат по PDF: поиск, факты, агенты и доступы в одном контуре. Облако или полностью у вас — файлы не уходят во внешние AI-сервисы.",
  painTitle: "Почему обычный чат по файлам нельзя пускать в компанию",
  painLead:
    "Облачный бот красиво отвечает. Юрист, HR и служба безопасности видят другую картину.",
  productTitle: "Одно рабочее пространство",
  productLead:
    "Искать, спрашивать, проверять и действовать. Сложность поиска и доступов остаётся под капотом.",
  stepsTitle: "Как это работает",
  stepsLead:
    "Документы остаются источником истины. Система ищет доказательства, а не додумывает.",
  demoTitle: "Как выглядит работа",
  demoLead: "Вопрос, проверка по источникам, разные ответы у разных ролей.",
  scenariosTitle: "Что меняется в работе",
  deployTitle: "Как разворачиваем",
  deployLead: "Один продукт. Данные организации не смешиваются с чужими.",
  craftTitle: "Почему одного чата мало",
  craftLead:
    "Готовый чат по файлам красиво додумывает. В PinWiki шесть отдельных решений — каждое закрывает ошибку, которую сотрудник замечает сразу.",
  waitlistTitle: "Запросить пилот",
  waitlistLead:
    "Оставьте рабочий email — свяжемся и покажем систему на демо-контуре или обсудим пилот на ваших документах.",
  wikiSlidesTitle: "Факты, версии и связи",
  wikiSlidesLead:
    "Документ остаётся источником. Wiki показывает, что из него следует — и где тексты не сходятся.",
  agentSlidesTitle: "Агенты по документам",
  agentSlidesLead:
    "Не диалог: сравнение версий и готовые правки черновика по норме.",
  carouselPrev: "Предыдущий слайд",
  carouselNext: "Следующий слайд",
  footerNote:
    "Ответы только из разрешённых документов, со ссылкой на первоисточник.",
} as const;

export const landingNav = [
  { href: "#product", label: "Продукт" },
  { href: "#scenarios", label: "Сценарии" },
  { href: "#deploy", label: "Развёртывание" },
  { href: "#waitlist", label: "Заявка" },
] as const;

export const landingSteps = [
  {
    n: "01",
    title: "Документы",
    text: "Регламенты, договоры, инструкции. Версии хранятся, а не затираются последним вложением из почты.",
  },
  {
    n: "02",
    title: "Факты",
    text: "Из текстов собираются утверждения. Если в документах разные цифры — оба факта на виду, без сглаживания.",
  },
  {
    n: "03",
    title: "Вопрос",
    text: "Сотрудник спрашивает своими словами: сколько дней отпуска, какой срок в договоре, что действует сейчас.",
  },
  {
    n: "04",
    title: "Ответ со ссылкой",
    text: "Каждое утверждение ведёт к странице документа. Нет источника — в ответе нет факта.",
  },
] as const;

export const landingTrust = [
  {
    title: "Доступ до поиска",
    text: "Запрещённый файл не попадает ни в выдачу, ни в ответ.",
  },
  {
    title: "Ссылка на страницу",
    text: "Нет первоисточника — в ответе нет факта. Источник можно открыть.",
  },
  {
    title: "Споры на виду",
    text: "Разные цифры в документах не сглаживаются в одну удобную.",
  },
  {
    title: "Cloud или On-Prem",
    text: "Изолированный контур или полная установка у вас, без внешних AI.",
  },
] as const;

export const landingPain = [
  {
    title: "Додумывает",
    text: "Если в файлах дыра, бот дописывает правдоподобное. Сотрудник принимает это за норму.",
  },
  {
    title: "Смешивает доступы",
    text: "Оклад коллеги и чужой договор всплывают «по смыслу». Стажёр видит то же, что юрист.",
  },
  {
    title: "Цепляется за старое",
    text: "Политика 2023 лежит рядом с 2025. Ответ приходит из вчерашнего вложения.",
  },
  {
    title: "Прячет спор",
    text: "В договоре три месяца, в оффере два — бот оставляет одну цифру и не говорит, что не сходится.",
  },
] as const;

export const landingProduct = [
  {
    id: "search",
    title: "Поиск",
    text: "По словам и по смыслу. Только в документах, которые сотруднику открыты.",
  },
  {
    id: "chat",
    title: "Чат",
    text: "Быстрый ответ или глубокий разбор. Рядом — страница источника, не пересказ.",
  },
  {
    id: "documents",
    title: "Документы",
    text: "Версии хранятся. Доступ — сотруднику, группе или отделу, до поиска и чата.",
  },
  {
    id: "wiki",
    title: "Wiki",
    text: "Факты и связи из текстов. Противоречие — оба утверждения на виду, не одно.",
  },
  {
    id: "agents",
    title: "Агенты",
    text: "Разбор, сравнение и доработка документа по норме — задача, а не диалог.",
  },
] as const;

export const landingWikiSlides = [
  {
    id: "facts",
    title: "Факты и правки",
    text: "Два числа об одном и том же — оба на виду. Правка Wiki помечается «изменён», документ не меняется.",
  },
  {
    id: "versions",
    title: "Версии документа",
    text: "Новый файл не затирает старый. В поиске и чате — текущая версия, история остаётся на вкладке.",
  },
  {
    id: "relations",
    title: "Связи",
    text: "Сущность связана со стороной и договором. Замена политики видна как связь, а не молчаливая подмена.",
  },
] as const;

export const landingAgentSlides = [
  {
    id: "compare",
    title: "Сравнение",
    text: "Два файла или две версии рядом: что общего и где расходятся сроки, стороны, формулировки.",
  },
  {
    id: "revise",
    title: "Доработка",
    text: "Готовые замены в черновике по норме. Принятые правки сохраняются новой версией документа.",
  },
] as const;

export const landingDemos = [
  {
    id: "chat",
    title: "Чат с источниками",
    text: "Ответ по абзацам, рядом — страница документа. Можно проверить, откуда взялась цифра.",
  },
  {
    id: "wiki",
    title: "Wiki не прячет спор",
    text: "Два факта об одном и том же — оба в карточке. Система говорит «не сходится», а не выбирает удобное.",
  },
  {
    id: "access",
    title: "Доступ до поиска",
    text: "Юрист видит договор, стажёр — нет. Запрещённый файл не попадает ни в выдачу, ни в ответ.",
  },
  {
    id: "agents",
    title: "Агенты по документам",
    text: "Разбор файла, сравнение двух версий, правки черновика по норме. Не ещё один чат.",
  },
] as const;

export const landingScenarios = [
  {
    title: "HR",
    text: "Новичок спрашивает про отпуск и испытательный — ответ из действующих приказов, не из пересказа в чате.",
  },
  {
    title: "Юристы",
    text: "Срок и стороны с цитатой на версию договора. Черновик сравнивается с нормой, а не с «кажется, в том PDF».",
  },
  {
    title: "Поддержка",
    text: "Клиентская процедура и внутренняя инструкция не смешиваются: сотрудник видит только своё.",
  },
  {
    title: "ИБ и комплаенс",
    text: "Конфиденциальные файлы остаются в контуре. Аудит показывает, на каких источниках построен ответ.",
  },
] as const;

export const landingCraft = [
  {
    n: "01",
    title: "Найти тот абзац, а не похожий файл",
    problem: "Человек спрашивает своими словами, в документе — другие формулировки.",
    gain: "В ответе фрагмент, где цифра действительно написана, а не соседний регламент «на ту же тему».",
  },
  {
    n: "02",
    title: "Не подмешать чужое",
    problem: "Оклад коллеги и чужой договор не должны всплыть «по смыслу».",
    gain: "Сотрудник видит только открытые ему документы. Запрещённое не попадает ни в выдачу, ни в ответ.",
  },
  {
    n: "03",
    title: "Не принять выдумку за факт",
    problem: "Модель охотно дописывает правдоподобное, если в файлах дыра.",
    gain: "Нет ссылки на страницу — в ответе нет утверждения. Источник можно открыть и проверить.",
  },
  {
    n: "04",
    title: "Не выбрать «удобную» цифру",
    problem: "В договоре три месяца, в оффере два — бот обычно оставляет одно.",
    gain: "Оба факта на виду. Система говорит, что не сходится, и не назначает победителя.",
  },
  {
    n: "05",
    title: "Не тормозить на простом и не сдаваться на сложном",
    problem: "«Сколько дней отпуска» не должен идти в долгий разбор, а сравнение двух политик — не должен останавливаться на первом файле.",
    gain: "В том же чате: быстрый ответ по словам или глубокий разбор в несколько проходов.",
  },
  {
    n: "06",
    title: "Не отвечать по устаревшей версии",
    problem: "Политика 2023 и 2025 лежат рядом, вопрос — «что действует сейчас».",
    gain: "Выше более новый документ. В ответе — страница актуальной версии, не вложение из прошлого года.",
  },
] as const;

export const landingDeploy = [
  {
    title: "Cloud",
    text: "Изолированный контур каждой организации. Данные компаний не смешиваются.",
  },
  {
    title: "On-Premise",
    text: "Полная установка у вас. Весь конвейер может работать без интернета.",
  },
] as const;

export const waitlistCopy = {
  nameLabel: "Имя",
  emailLabel: "Рабочий email",
  companyLabel: "Компания",
  deploymentLabel: "Развёртывание",
  cloud: "Облако",
  onprem: "Своё железо",
  commentLabel: "Комментарий",
  commentHint: "Необязательно",
  submit: "Запросить пилот",
  pending: "Отправляем…",
  success: "Заявка принята. Напишем, чтобы согласовать пилот.",
  duplicate: "Эта почта уже в списке. Напишем, чтобы согласовать пилот.",
  error: "Не удалось сохранить заявку. Попробуйте ещё раз.",
  invalidName: "Укажите имя",
  invalidEmail: "Укажите рабочий email",
  invalidCompany: "Укажите компанию",
  invalidDeployment: "Выберите вариант развёртывания",
  invalidComment: "Комментарий слишком длинный",
} as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_MAX = 80;
const COMPANY_MAX = 120;
const COMMENT_MAX = 1000;

export function waitlistHref(): string {
  return "/#waitlist";
}

export function wrapSlideIndex(
  index: number,
  length: number,
  delta: number,
): number {
  if (length <= 0) {
    return 0;
  }
  return ((index + delta) % length + length) % length;
}

export function isPublicLandingPath(pathname: string): boolean {
  return pathname === "/";
}

export function parseWaitlistDeployment(
  raw: string,
): WaitlistDeployment | null {
  if (raw === "CLOUD" || raw === "ONPREM") {
    return raw;
  }
  return null;
}

export function parseWaitlistLead(input: {
  name: string;
  email: string;
  company: string;
  deployment: string;
  comment: string;
}): { ok: true; value: WaitlistInput } | { ok: false; error: string } {
  const name = input.name.trim().replace(/\s+/g, " ");
  const email = input.email.trim().toLowerCase();
  const company = input.company.trim().replace(/\s+/g, " ");
  const deployment = parseWaitlistDeployment(input.deployment.trim());
  const commentRaw = input.comment.trim().replace(/\s+/g, " ");

  if (!name || name.length > NAME_MAX) {
    return { ok: false, error: waitlistCopy.invalidName };
  }
  if (!email || email.length > 254 || !EMAIL_RE.test(email)) {
    return { ok: false, error: waitlistCopy.invalidEmail };
  }
  if (!company || company.length > COMPANY_MAX) {
    return { ok: false, error: waitlistCopy.invalidCompany };
  }
  if (!deployment) {
    return { ok: false, error: waitlistCopy.invalidDeployment };
  }
  if (commentRaw.length > COMMENT_MAX) {
    return { ok: false, error: waitlistCopy.invalidComment };
  }

  return {
    ok: true,
    value: {
      name,
      email,
      company,
      deployment,
      comment: commentRaw.length > 0 ? commentRaw : null,
    },
  };
}

export function isWaitlistDuplicateError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code: string }).code === "P2002"
  );
}

export function waitlistSuccessMessage(duplicate: boolean): string {
  return duplicate ? waitlistCopy.duplicate : waitlistCopy.success;
}
